import { Config, ApiKeys, AuthResponse, ConfigResponse, PreviewData } from '../types';

const API_BASE = 'https://vestaboard-api.jterlesk.workers.dev/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth-token');

  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }

  return response.json();
}

export const api = {
  async login(username: string, password: string): Promise<AuthResponse> {
    console.log('api: Starting login request', { username, endpoint: `${API_BASE}/auth/login` });
    try {
      const result = await request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      console.log('api: Login request successful', result);
      return result;
    } catch (error) {
      console.error('api: Login request failed', error);
      throw error;
    }
  },

  async getConfig(): Promise<ConfigResponse> {
    return request<ConfigResponse>('/config');
  },

  async updateConfig(config?: Partial<Config>, apiKeys?: Partial<ApiKeys>): Promise<void> {
    return request<void>('/config', {
      method: 'PUT',
      body: JSON.stringify({ config, apiKeys }),
    });
  },

  async validateApiKeys(apiKeys: Partial<ApiKeys>): Promise<Record<string, boolean>> {
    const response = await request<{ validationResults: Record<string, boolean> }>('/config/validate', {
      method: 'POST',
      body: JSON.stringify({ apiKeys }),
    });
    return response.validationResults;
  },

  async generatePreview(config: Config): Promise<PreviewData> {
    return request<PreviewData>('/preview', {
      method: 'POST',
      body: JSON.stringify({ config }),
    });
  },

  async sendToVestaboard(): Promise<{ success: boolean; message: string; displayData: any }> {
    return request<{ success: boolean; message: string; displayData: any }>('/send-to-vestaboard', {
      method: 'POST',
    });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return request<void>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },
};

export { ApiError };